<template>
  <section class="m-4 d-flex flex-column"
    data-name="megaport-user"
    data-testId="megaport-user">
    <!-- Heading / nav -->

    <div class="mb-4"
      role="presentation">
      <div class="d-flex align-items-center justify-content-between"
        role="presentation">
        <img :src="activeUser.photo"
          onerror="this.style.display = 'none'"
          class="userPic rounded-circle mr-2"
          alt="User icon">
        <h3>{{ activeUser.display_name }}</h3>
      </div>
    </div>

    <!-- User details -->

    <div class="mb-4"
      role="presentation">
      <ul class="list-group">
        <li class="list-group-item d-flex justify-content-between align-items-center">
          Email
          <span role="presentation">
            <a :href="`mailto:${activeUser.email}`">{{ activeUser.email }}</a>
          </span>
        </li>
        <li class="list-group-item d-flex justify-content-between align-items-center">
          Created
          <span role="presentation">
            {{ luxonFormat(activeUser.created) }}
          </span>
        </li>
        <li class="list-group-item d-flex justify-content-between align-items-center">
          Last login
          <span role="presentation">
            {{ luxonFormat(activeUser.last_login) }}
          </span>
        </li>
      </ul>
    </div>

    <hr>

    <!-- API keys -->

    <div class="mb-4"
      role="presentation">
      <h3>API Keys</h3>
      <p v-if="!apiKeys.length"
        class="alert alert-secondary">
        No API keys found
      </p>

      <div v-else
        class="list-group">
        <div v-for="apiKey in apiKeys"
          :key="apiKey.id"
          class="list-group-item">
          <div class="d-flex justify-content-between align-items-start"
            role="presentation">
            <strong role="heading">{{ apiKey.description }}</strong>
            <button type="button"
              class="badge badge-danger ml-2 flex-shrink-0"
              @click="handleDeleteKey(apiKey.id)">
              Delete
            </button>
          </div>
          <small class="text-muted">Created {{ luxonFormat(apiKey.created) }}</small>
          <small v-if="apiKey.last_used"
            class="text-muted d-block">Last used {{ apiKey.last_used }}</small>
          <small v-else
            class="text-muted d-block">Last used Never</small>
        </div>
      </div>
    </div>

    <!-- API key form -->

    <div class="mb-4"
      role="presentation">
      <h4>Generate new API key</h4>
      <b-form class="mt-2 d-flex flex-wrap">
        <b-form-group label="Description"
          class="mr-2 flex-grow-1">
          <b-form-input v-model="apiKeyDescription"
            data-name="description-input"
            size="sm" />
        </b-form-group>
      </b-form>
      <button type="button"
        data-name="generate-button"
        class="btn btn-primary btn-sm"
        :disabled="!isKeyValid"
        @click="handleGenerateKey">
        Generate
      </button>
    </div>

    <hr>

    <!-- TACACS password -->

    <div class="mb-4"
      role="presentation">
      <div class="d-flex justify-content-between mb-2">
        <h3 class="my-auto">
          TACACS Password
        </h3>
        <div role="button"
          class="btn btn-primary btn-sm"
          @click="showTacacsPasswordModal">
          <template v-if="activeUser.password_hash">
            Change
          </template>
          <template v-else>
            Set
          </template>
        </div>
      </div>

      <p v-if="!activeUser.password_hash"
        class="alert alert-secondary">
        No TACACS password
      </p>

      <div v-else
        class="list-group">
        <div class="list-group-item">
          <div role="presentation">
            <strong role="heading">Current hash</strong>
          </div>
          <p class="mt-1 blur overflow-wrap"
            role="text">
            {{ activeUser.password_hash }}
          </p>
        </div>
      </div>

      <user-tacacs-password-modal ref="tacacsPasswordModal"
        @change-tacacs-password="handleChangeTacacsPassword" />
    </div>

    <hr>

    <!-- SSH Keys -->

    <div class="mb-4"
      role="presentation">
      <div class="d-flex justify-content-between mb-2">
        <h3 class="my-auto">
          SSH Keys
        </h3>
        <div role="button"
          class="btn btn-primary btn-sm"
          @click="showSshKeysModal">
          Manage
        </div>
      </div>

      <p v-if="!activeUser.authorized_keys"
        class="alert alert-secondary">
        No SSH keys
      </p>

      <div v-else
        class="list-group">
        <div class="list-group-item">
          <div role="presentation">
            <strong role="heading">Active keys</strong>
          </div>
          <pre class="mt-1 blur ssh-keys"
            role="text">{{ activeUser.authorized_keys }}</pre>
        </div>
      </div>

      <user-ssh-keys-modal ref="sshKeysModal"
        :active-user="activeUser"
        @update-ssh-keys="handleUpdateSshKeys" />
    </div>

    <hr>

    <!-- Roles -->
    <div v-mp-access:[userGroups]="permission"
      class="mb-4"
      role="presentation">
      <h3>Roles</h3>
      <b-form-group>
        <b-form-checkbox v-for="group in activeUser.groups"
          :key="group.id"
          v-model="selectedGroups"
          name="user-group"
          :value="group.id">
          {{ group.name }}
        </b-form-checkbox>

        <button type="button"
          class="mt-3 btn btn-primary btn-sm"
          @click="handleUpdateGroups">
          Save changes
        </button>
      </b-form-group>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { luxonFormat } from '@/filters/luxon-helpers'

import UserTacacsPasswordModal from '@/components/user/UserTacacsPasswordModal'
import UserSshKeysModal from '@/components/user/UserSshKeysModal'

export default {
  name: 'UserProfile',

  components: {
    'user-tacacs-password-modal': UserTacacsPasswordModal,
    'user-ssh-keys-modal': UserSshKeysModal,
  },

  props: {
    activeUser: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
  },

  emits: [
    'update-groups',
    'generate-key',
    'delete-key',
    'change-tacacs-password',
    'update-ssh-keys',
  ],

  data() {
    return {
      isLoading: true,
      apiKeyDescription: '',
      selectedGroups: [],
      permission: ['admin'],
    }
  },

  computed: {
    ...mapGetters('user', ['userGroups']),
    ...mapState('user', ['account']),
    isKeyValid() {
      return !!this.apiKeyDescription.length
    },
    apiKeys() {
      return this.activeUser.apikeys || []
    },
  },

  watch: {
    $route: function () {
      // regenerate the selected checkboxes on route change
      this.generateUserGroups()
      // reset the form
      if (this.activeUser) this.resetApiKeyForm()
    },
  },

  methods: {
    luxonFormat(value) {
      return luxonFormat(value)
    },
    resetApiKeyForm() {
      this.apiKeyDescription = ''
    },
    generateUserGroups() {
      this.selectedGroups = this.activeUser.groups
        .filter(group => {
          return !!group.member
        })
        .map(group => {
          return group.id
        })
    },
    handleUpdateGroups() {
      const updatedGroups = {}
      this.activeUser.groups.forEach(group => {
        updatedGroups[group.id] = this.selectedGroups.includes(group.id)
      })
      this.$emit('update-groups', updatedGroups)
    },
    handleGenerateKey() {
      this.$emit('generate-key', {
        description: this.apiKeyDescription,
      })
      this.resetApiKeyForm()
    },
    handleDeleteKey(keyId) {
      this.$emit('delete-key', keyId)
    },
    showTacacsPasswordModal() {
      this.$refs.tacacsPasswordModal.show()
    },
    handleChangeTacacsPassword(event) {
      this.$emit('change-tacacs-password', event)
    },
    showSshKeysModal() {
      this.$refs.sshKeysModal.show()
    },
    handleUpdateSshKeys(event) {
      this.$emit('update-ssh-keys', event)
    },
  },
}
</script>

<style lang="scss" scoped>
.userPic {
  width: 40px;
  height: 40px;
}
.heading {
  background-color: var(--dark);
  color: var(--white);
}
.blur {
  filter: blur(4px);
  &:hover {
    filter: blur(0px);
  }
}
hr {
  border-top: 0.5px solid var(--gray);
  margin-bottom: 1.5rem;
  width: 100%;
}
.overflow-wrap {
  overflow-wrap: anywhere;
}
.ssh-keys {
  overflow-x: auto;
}
</style>
